import { Button } from "@components/Buttons"
import FieldControl from "@components/FieldControl"
import { Checkbox, Input } from "@components/FormFields"
import { useIntl } from "@components/Intl"
import { Box } from "@components/Layout"
import { useNotificationsUpdate } from "@components/Notifications"
import { NewsletterSignupMutationVariables } from "@generated"
import { getGraphQLClient } from "@lib/graphql-client"
import { useMutation } from "@tanstack/react-query"
import { Form as FormikForm, Formik, FormikProps } from "formik"
import { rem } from "polished"
import React, { useState } from "react"
import { schema } from "./schema"
import { genericPushToTrackingLayer } from "@lib/data-layer-events/generic-push"

interface Props {}
interface Values {
  firstName: string
  lastName: string
  email: string
}

const { sdk } = getGraphQLClient()

const Form: React.FC<Props> = () => {
  const { locale, messages } = useIntl()
  const [privacyStatement, setPrivacyStatement] = useState(false)
  const { handleException, addNotification } = useNotificationsUpdate()
  const { mutateAsync: newsletterSignup } = useMutation(
    async ({ input }: NewsletterSignupMutationVariables) => {
      return await sdk.NewsletterSignup({
        input: {
          choicesNewsletter: true,
          email: input.email,
          firstName: input.firstName,
          lastName: input.lastName,
        },
      })
    }
  )

  return (
    <Formik
      initialValues={{ firstName: "", lastName: "", email: "" }}
      validationSchema={schema[locale]}
      onSubmit={async (values, actions) => {
        const input = {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
        }

        await newsletterSignup({ input }).catch((error) => {
          handleException(error)
        })

        genericPushToTrackingLayer("form_submit", "newsletter_signup")
        addNotification({
          type: "success",
          text: messages.newsletter.submissionSuccess,
        })
        setPrivacyStatement(false)
        actions.resetForm()
        actions.setSubmitting(false)
      }}
    >
      {({
        handleChange,
        handleBlur,
        values,
        errors,
        isSubmitting,
        touched,
      }: FormikProps<Values>) => (
        <FormikForm>
          <Box maxWidth={["100%", rem(500)]}>
            <FieldControl label={messages.newsletter.firstName} maxWidth="100%">
              <Input
                name="firstName"
                type="text"
                placeholder={messages.newsletter.firstName}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.firstName}
                errorMsg={
                  errors.firstName && touched.firstName
                    ? errors.firstName
                    : undefined
                }
              />
            </FieldControl>
            <FieldControl label={messages.newsletter.lastName} maxWidth="100%">
              <Input
                name="lastName"
                type="text"
                placeholder={messages.newsletter.lastName}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.lastName}
                errorMsg={
                  errors.lastName && touched.lastName
                    ? errors.lastName
                    : undefined
                }
              />
            </FieldControl>
            <FieldControl label={messages.newsletter.email} maxWidth="100%">
              <Input
                name="email"
                type="email"
                placeholder={messages.newsletter.email}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.email}
                errorMsg={
                  errors.email && touched.email ? errors.email : undefined
                }
              />
            </FieldControl>
            <FieldControl label="" maxWidth="100%">
              <Box width="100%">
                <Checkbox
                  checked={privacyStatement}
                  onClick={() => setPrivacyStatement(!privacyStatement)}
                >
                  {messages.checkout.summary.agreeWithOur}{" "}
                  <a
                    target="blank"
                    href={messages.navigation.privacyStatement.url}
                  >
                    {messages.checkout.summary.privacyStatement}
                  </a>
                </Checkbox>
              </Box>
            </FieldControl>
            <Box py={3} display="flex" justifyContent="flex-end">
              <Button
                variant="purple"
                state={isSubmitting ? "loading" : undefined}
                disabled={!privacyStatement}
                maxWidth={200}
                type="submit"
              >
                {messages.newsletter.submit}
              </Button>
            </Box>
          </Box>
        </FormikForm>
      )}
    </Formik>
  )
}

export default Form
